/**
 * The guest draft: one rider document kept in the browser for someone who has NOT signed in
 * (SCRUM-250). It exists so `/editor` can be opened with no account at all — the product explains
 * itself in thirty seconds of 3D, and until this landed we put a registration form in front of the
 * only screen that does the explaining.
 *
 * Deliberately its own tiny module next to `io-delete-policy.ts`, and for the same reason:
 * `localStorage` is a browser-specific store, while `rider.ts` is the engine/platform-agnostic core
 * a future mobile client reuses untouched (`docs/V2_ARCHITECTURE.md`, the engine-agnostic seam). A
 * native client would back the same idea with its own store.
 *
 * THE OWNERSHIP RULE, in the owner's own words (2026-08-20, SCRUM-300): «если гость вошел, сделал,
 * решил залогиниться или зарегистрироваться — черновик из его сессии передаётся. Если не вошёл и не
 * зарегистрировался — умирает с его сессией. Никогда не передаётся в новый райдер: чистый.»
 *
 * Three consequences, and every line below follows from them:
 *
 *  1. **The draft lives in `sessionStorage`, not `localStorage`.** "Dies with his session" is what
 *     that store means: it survives a reload and every in-tab navigation (so the trip through the
 *     login form loses nothing), and it is gone when the tab closes. `localStorage` outlives the
 *     visitor, which is how a stranger's stage plot turned up in the owner's own editor.
 *  2. **Any sign-in transfers it.** Not only the register-after-Save path: logging into an existing
 *     account carries the work in too. The editor's mount does the transfer — restore, then write to
 *     the account — and clears the draft once the write lands.
 *  3. **A signed-in New rider is therefore always empty.** There is nothing left to restore: either
 *     the draft was transferred into the account, or it died with the session that made it.
 *
 * This replaced a **pending-save flag** (`scb.guestDraftPendingSave`), which existed only to tell
 * "they pressed Save, write it now" apart from "restore it and wait". Under the rule above those are
 * one case, so the flag, its stale-flag recovery (SCRUM-259) and the whole class of bug where an
 * unrelated later sign-in fired somebody's old intent are simply gone rather than fixed again.
 *
 * Nothing here ever throws: private browsing, a full quota and locked-down embeds all have to
 * degrade into "no draft", never into a broken editor.
 */

import type { Rider } from "@scb/schema";
import { getStoredUser } from "../api/http";
import { accountWrites } from "../api/write-gate";

const DRAFT_KEY = "scb.guestDraft";
/** Set immediately before the caller renders a just-restored draft, cleared immediately after that
 *  render succeeds — see `takeGuestDraftForRestore`/`confirmGuestDraftApplied` below. */
const APPLYING_KEY = "scb.guestDraftApplying";

/** Чей документ лежит в состоянии «ждёт подтверждения почты» — иначе следующий человек,
 *  зарегистрировавшийся в этом же браузере, увидел бы чужой райдер (ровно тот дефект, из-за которого
 *  черновик гостя и переехал в `sessionStorage`). */
const OWNER_KEY = "scb.guestDraft.owner";

/**
 * ДВА СОСТОЯНИЯ, И У НИХ РАЗНЫЕ ХРАНИЛИЩА. Правило владельца (SCRUM-300) писалось про ЧИСТОГО гостя
 * и остаётся дословным: «не вошёл и не зарегистрировался — умирает с его сессией», то есть
 * `sessionStorage`, который переживает перезагрузку и переходы внутри вкладки, но не её закрытие.
 *
 * Второе состояние появилось вместе с решением А (`docs/V2_MONEY_TRACK.md` §5а, владелец 14.10.2026):
 * человек нажал «Сохранить», ввёл почту и ЖДЁТ подтверждения. Он уже вошёл, но сервер его запись
 * отклоняет (`api/write-gate.ts`), и документ обязан дожить до того, как он нажмёт в письме. Ссылка
 * из письма открывается в НОВОЙ вкладке — то же основание, что записано в `offer/offer-auto-send.ts`,
 * — а `sessionStorage` новой вкладке не виден. Поэтому ждущий подтверждения документ живёт в
 * `localStorage`, и это НЕ отмена правила SCRUM-300, а второе состояние рядом с ним (находка
 * критика 3, 14.10.2026).
 *
 * Чистый гость в `localStorage` не пишет НИКОГДА, и из него не читает: иначе чужой стейдж-план
 * дождался бы следующего посетителя — тот самый дефект, которым правило и вызвано.
 */
function pendingAccount(): string | undefined {
  try {
    const id = getStoredUser()?.id;
    if (!id) return undefined;
    return accountWrites() === "accepts" ? undefined : id;
  } catch {
    return undefined;
  }
}

const store = (): Storage => (pendingAccount() ? localStorage : sessionStorage);

/** Второе хранилище — то, из которого читают при ПЕРЕХОДЕ состояния: человек начал гостем (документ
 *  в сессии), нажал «Сохранить», ввёл почту и стал «ждущим». Возвращает `undefined` для чистого
 *  гостя: ему из `localStorage` читать нечего и нельзя. */
function previousStore(): Storage | undefined {
  return pendingAccount() ? sessionStorage : undefined;
}

/** Строка черновика из того хранилища, которое соответствует состоянию, с откатом на предыдущее.
 *  В состоянии «ждёт подтверждения» документ принимается только если он помечен ЭТИМ аккаунтом. */
function readDraftRaw(): string | null {
  const account = pendingAccount();
  if (account !== undefined) {
    try {
      const owner = localStorage.getItem(OWNER_KEY);
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw !== null && owner === account) return raw;
    } catch {
      /* нет хранилища — пробуем предыдущее */
    }
  }
  const previous = previousStore();
  if (previous) {
    try {
      return previous.getItem(DRAFT_KEY);
    } catch {
      return null;
    }
  }
  try {
    return store().getItem(DRAFT_KEY);
  } catch {
    return null;
  }
}

/** Пишет в хранилище состояния и убирает копию из второго: двух правд о документе быть не должно. */
function writeDraftRaw(raw: string): void {
  const account = pendingAccount();
  store().setItem(DRAFT_KEY, raw);
  if (account !== undefined) {
    try {
      localStorage.setItem(OWNER_KEY, account);
    } catch {
      /* пометка не записалась — хуже только тем, что документ не узнается после смены человека */
    }
  }
  const previous = previousStore();
  if (previous) {
    try {
      previous.removeItem(DRAFT_KEY);
    } catch {
      /* копия в предыдущем хранилище не мешает: читают сперва из хранилища состояния */
    }
  }
}

/** Убирает ключ из ОБОИХ хранилищ: перенос в аккаунт — это перемещение, и оставленный след
 *  возвращался бы на следующем входе в редактор (SCRUM-300). */
function removeEverywhere(key: string): void {
  for (const target of [sessionStorage, localStorage]) {
    try {
      target.removeItem(key);
    } catch {
      /* хранилище недоступно — удалять нечего */
    }
  }
}

/** Guard against a runaway document filling the origin's quota — a stage plot is kilobytes, so
 *  anything past this is a bug or an import, and dropping it silently beats bricking `localStorage`
 *  for the theme and the print options that share it. */
const MAX_DRAFT_BYTES = 2_000_000;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Drops any non-object entry from a `children`/`sets` array, recursing into the survivors' own
 * `children`/`sets` — `localStorage` is user-writable and outlives every build, so it is untrusted
 * input exactly like a request body, and a single stray `null` (a partial write, a race, an old
 * incompatible build) must not reach `nodeSig` (`scene.ts`), which assumes every array entry is a
 * real node object — the same invariant the API path enforces through schema validation before a
 * rider ever reaches the renderer.
 *
 * Deliberately PRUNING, not validating: an entry that IS an object is kept exactly as it arrived, so
 * a document written by an older build is still restored as faithfully as `readGuestDraft`'s own
 * comment always promised — only the one shape that is known to crash the renderer is removed.
 */
function pruneMalformedNodes(node: Record<string, unknown>): void {
  if (Array.isArray(node.children)) {
    const kept = node.children.filter(isPlainObject);
    node.children = kept;
    for (const child of kept) pruneMalformedNodes(child);
  }
  if (Array.isArray(node.sets)) {
    const kept = node.sets.filter(isPlainObject);
    node.sets = kept;
    for (const set of kept) pruneMalformedNodes(set);
  }
}

/** Reads the stored draft, or `undefined` when there is none, when it is unparseable, or when the
 *  store is unavailable. Shape is NOT validated beyond "it is an object with children" — the editor
 *  runs `ensureRiderDefaults` over whatever comes back, exactly as it does for a rider loaded from
 *  the API, and a document written by an older build is worth restoring imperfectly rather than
 *  discarding. `pruneMalformedNodes` removes the one shape that is worse than imperfect: a non-object
 *  array entry that crashes the renderer outright (SCRUM-250 critic finding 1). */
export function readGuestDraft(): Rider | undefined {
  try {
    const raw = readDraftRaw();
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!isPlainObject(parsed)) return undefined;
    if (!Array.isArray(parsed.children)) return undefined;
    pruneMalformedNodes(parsed);
    return parsed as unknown as Rider;
  } catch {
    /* absent, corrupt, or no store — the editor simply starts empty */
    return undefined;
  }
}

/** Last serialisation actually written, so an unchanged document costs no storage traffic. The editor
 *  calls `writeGuestDraft` from `renderAll`, which also runs for pure-UI renders (selection, tabs),
 *  and those must not each turn into a `localStorage` write. */
let lastWritten: string | undefined;
/** В каком хранилище лежит то, что записано последним. Переход «гость → ждёт подтверждения» меняет
 *  хранилище, а строка документа при этом та же — без этого признака запись была бы пропущена как
 *  «не изменилось», и документ остался бы только в сессии, невидимый для вкладки из письма. */
let lastWrittenPlace: "session" | "account" | undefined;

/** What `writeGuestDraft` actually managed to do — the caller (`editor-page.ts`) uses this to warn
 *  the person exactly once when their edits have stopped reaching storage (SCRUM-250 critic finding
 *  2): `"unchanged"` is the common no-op case (a pure-UI render) and is never worth mentioning,
 *  `"written"` is the ordinary success case, and `"failed"` is the case that used to be silent — a
 *  full quota (`QuotaExceededError`), an unavailable store, or a document past `MAX_DRAFT_BYTES`. */
export type GuestDraftWriteResult = "written" | "unchanged" | "failed";

/** Persists the draft. Called on every edit, so it must stay cheap and must never interrupt the edit
 *  it is recording: an over-quota write is dropped and the in-memory document carries on — the EDIT
 *  itself always still stands, only its persistence across a reload is at risk, which is what the
 *  `"failed"` result is for. */
export function writeGuestDraft(rider: Rider): GuestDraftWriteResult {
  try {
    const raw = JSON.stringify(rider);
    const place = pendingAccount() ? "account" : "session";
    if (raw === lastWritten && place === lastWrittenPlace) return "unchanged";
    if (raw.length > MAX_DRAFT_BYTES) return "failed";
    writeDraftRaw(raw);
    lastWritten = raw;
    lastWrittenPlace = place;
    return "written";
  } catch {
    /* quota or unavailable store — the edit itself still stands, only the persistence of it failed */
    return "failed";
  }
}

/** Forgets the draft and its apply-mark. Called once the document has been written to an account —
 *  the transfer is a MOVE, and leaving the original behind is what let a stale copy come back on the
 *  next `/editor` mount (SCRUM-300) — and also when a draft is quarantined after crashing the app
 *  (see `takeGuestDraftForRestore`), where leaving the applying flag set would keep quarantining
 *  every FUTURE draft too. */
export function clearGuestDraft(): void {
  lastWritten = undefined; // or the next guest edit that happens to serialise identically is skipped
  lastWrittenPlace = undefined;
  removeEverywhere(DRAFT_KEY);
  removeEverywhere(APPLYING_KEY);
  removeEverywhere(OWNER_KEY);
}

/** Does a draft exist at all? Used by the route resolver to decide whether signing in should land
 *  the person back in the editor instead of on the riders list. Cheaper than parsing it. */
export function hasGuestDraft(): boolean {
  try {
    return readDraftRaw() !== null;
  } catch {
    return false;
  }
}

/**
 * The result of asking for the draft to restore: either the draft itself, or nothing — and if it's
 * nothing because a PREVIOUS attempt to apply it never finished, `discardedAfterCrash` says so, so
 * the caller can tell the person instead of restoring silently as if there had never been a draft.
 */
export interface GuestDraftForRestore {
  draft: Rider | undefined;
  /** True when a draft existed but was thrown away because applying it crashed the app last time
   *  (SCRUM-250 critic finding 1b) — trying again would crash identically. */
  discardedAfterCrash: boolean;
}

/**
 * Pruning malformed nodes (above) closes the ONE crash shape a critic actually found — it cannot
 * close shapes nobody has thought of yet. This is the second, general layer: a draft may never be
 * able to brick the app TWICE. `markGuestDraftApplying`/`confirmGuestDraftApplied` bracket the one
 * render that can throw (`renderAll` -> `syncFromRider` -> `nodeSig`, in `editor-page.ts`'s restore
 * path); if that render throws, control never reaches the confirm call, so the mark is still there on
 * the NEXT load — which is exactly how this function tells a "died mid-apply" draft apart from an
 * ordinary one and refuses to try it again.
 *
 * This is the ONLY place a draft is discarded for having crashed; `readGuestDraft` on its own always
 * just tries to restore what pruning left behind.
 */
export function takeGuestDraftForRestore(): GuestDraftForRestore {
  if (isGuestDraftApplyPending()) {
    clearGuestDraft(); // whatever this draft contains killed the app once already — don't retry it
    return { draft: undefined, discardedAfterCrash: true };
  }
  const draft = readGuestDraft();
  if (draft) markGuestDraftApplying();
  return { draft, discardedAfterCrash: false };
}

/** True when a previous `takeGuestDraftForRestore` call marked a draft as being applied and no
 *  matching `confirmGuestDraftApplied` ever ran — i.e. the render that followed never finished. */
function isGuestDraftApplyPending(): boolean {
  try {
    return store().getItem(APPLYING_KEY) === "1";
  } catch {
    return false;
  }
}

/** Marks "about to render this restored draft" — called by `takeGuestDraftForRestore` itself, and
 *  exported separately so the test suite can simulate a crash (mark, then never confirm) without
 *  actually throwing one. */
export function markGuestDraftApplying(): void {
  try {
    store().setItem(APPLYING_KEY, "1");
  } catch {
    /* a store that can't hold this flag can't hold the draft either — nothing to quarantine */
  }
}

/** Confirms the render that followed `takeGuestDraftForRestore` finished without throwing. Call this
 *  IMMEDIATELY after that render — anything placed between them is a window where a crash would be
 *  wrongly treated as a successful apply. */
export function confirmGuestDraftApplied(): void {
  try {
    store().removeItem(APPLYING_KEY);
  } catch {
    /* best effort */
  }
}
